import { createContext, useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useRequest } from "../../Hooks/useRequest";
import VisualizationArea from "./VisualizationArea";
import { useEndpointProvider } from '../../Providers/EndpointProvider';
import * as MdIcons from "react-icons/md";
import { useAuthProvider } from "../../Providers/AuthProvider";
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider";
import { 
	generalShortcutsAtom, 
	annotationShortcutsAtom,
	loadHotkeysFromConfigs 
} from "../../Pages/Data/Visualize/DataReview/Atoms/Hotkeys"
import { useRecoilCallback, useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import { currentPatientFileInfoAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/PatientFile";
import { activeAnnotationSessionAtom, annotationGroupsAtom, annotationsAtom, selectedAnnotationAtom, selectedAnnotationGroupAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/Annotations";
import { useOnMount } from "../../Hooks/useOnMount";
import { layoutGroupsAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/Layout";
import { pageManagerRegistry } from "../../Pages/Data/Visualize/DataReview/Data/PageManagerRegistry";
import { viewScaleRegistry } from "../../Pages/Data/Visualize/DataReview/Data/ViewScaleRegistry";
import { useModalProvider } from '../../Providers/ModalProvider';
import { useTableContext } from '../../Providers/TableProvider';
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../Components/MobergButton/MobergButton";
import { MobergIconSize } from "../../Components/MobergIcon/MobergIcon";
import { filterVirtualAnnotationGroups, useAnnotationService } from "../../Hooks/useAnnotationService";
import { useAnnotationShortcutService } from "../../Hooks/useAnnotationShortcutService";
import { eegMontagesAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/EEGMontage";
import { fileScaleRegistry } from "../../Pages/Data/Visualize/DataReview/Data/FileScaleRegistry";
import { useEEGMontageService } from "../../Hooks/useEEGMontageService";
import { DataSource } from "../../Pages/Data/Visualize/DataReview/Types/DataSource";
import { useBackendLinksProvider } from "../../Providers/BackendLinksProvider";
import { Deployment, useEnvironmentVariablesProvider } from "../../Providers/EnvironmentVariablesProvider";
import { usePrefetchData } from "../../Pages/Data/Visualize/DataReview/Hooks/usePrefetchData"
import { useModalitiesProvider } from "../../Providers/ModalitiesProvider";
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks";
import { LiveReviewDelayWarning } from "../../Pages/Data/Visualize/DataReview/Components/Modals/LiveReviewDelayWarning"
import ConfirmationModal from "../../Components/ConfirmationModal/ConfirmationModal";
import { MobergTheme } from "../../Moberg";
import { VisualizationComponent } from "../../Pages/Data/Visualize/DataReview/VisualizationComponent";
import { modalityTimesAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/ModalityTimes";
import { useDataReviewPermissions } from "../../Hooks/useDataReviewPermissions";
import { useEMRDataProvider } from "../../Providers/EMRDataProvider";


export const VisualizationContext = createContext(null)
const isIncompleteChecklistItem = item => !item.loaded && !item.active

export function Visualization(props) {
	// TODO: is all of this necessary in the URL? No. But it gets Data Review on its own page, which is more important right now.
	let { patientID, patientIdentifier, siteName, dataObjectID, dataObjectName } = useParams()
	const location = useLocation()
	
	patientID = parseInt(patientID)
	dataObjectID = parseInt(dataObjectID)
	const patientName = location.state?.patientName || "Unknown"

	const navigate = useNavigate()
	const resetAnnotations = useResetRecoilState(annotationsAtom)
	const resetSelectedAnnotationGroup = useResetRecoilState(selectedAnnotationGroupAtom)
	const resetSelectedAnnotation = useResetRecoilState(selectedAnnotationAtom)
	const workspacesProvider = useWorkspacesProvider()
	const authProvider = useAuthProvider()
	const endpointProvider = useEndpointProvider()
	const { setAllPatientModalities } = useModalitiesProvider()
	const [currentPatientFileInfo, setPatientFileInfo] = useRecoilState(currentPatientFileInfoAtom)
	const setAnnotationGroups = useSetRecoilState(annotationGroupsAtom)
	const setActiveAnnotationSession = useSetRecoilState(activeAnnotationSessionAtom)
	const [layoutsLoaded, setLayoutLoaded] = useState(false)
	const setLayoutGroups = useSetRecoilState(layoutGroupsAtom)
	const setEEGMontages = useSetRecoilState(eegMontagesAtom)
	const [eegMontagesLoaded, setEEGMontagesLoaded] = useState(false)
	const [eegMontageMapping, setEEGMontageMapping] = useState()
	const [persystRecordingInfo, setPersystRecordingInfo] = useState()
	const [persystMontageDefinitions, setPersystMontageDefinitions] = useState()
	const [admissionInfo, setAdmissionInfo] = useState()
	const [dataObjectMetadata, setDataObjectMetadata] = useState()
	const [patientMedications, setPatientMedications] = useState()
	const [allMedications, setAllMedications] = useState()
	const [liveReviewWarningActive, setLiveReviewWarningActive] = useState(false)
	const { createAnnotationGroup } = useAnnotationService()
	const { setToastProps } = useTableContext()
	const { canManageDataReview } = useDataReviewPermissions()
	const { deserializeQueryMontage } = useEEGMontageService()
	const { getAnnotationShortcuts } = useAnnotationShortcutService()
	const { LINKS } = useBackendLinksProvider()
	const { deployment, environmentVariables } = useEnvironmentVariablesProvider()
	const emrDataProvider = useEMRDataProvider()


	const MINIMUM_RECORDING_DURATION_LIVE_REVIEW = environmentVariables.LIVE_REVIEW_REAL_TIME_DELAY_MS + environmentVariables.LIVE_REVIEW_READ_AHEAD_BUFFER_MS

	const isDevelopment = deployment === Deployment.DEVELOPMENT
	const prefetchDisabled = deployment === Deployment.CLINICAL

	const isDemo = workspacesProvider.selectedWorkspace === "DEMO"

	const [ready, setReady] = useState(false)
	const [completing, setCompleting] = useState(false)
	const completionStarted = useRef(false)
	const [showTimeoutWarning, setShowTimeoutWarning] = useState(false)

	// The staged-loading effect below runs on every render (it has no dependency array). A failed
	// preload resets usePrefetchData back to "idle", so without this flag the effect would call
	// startPrefetch again on the next render and loop failure -> modal -> retry forever.
	const [prefetchStarted, setPrefetchStarted] = useState(false)

	const { close, createModal } = useModalProvider()
	const { prefetchMessage, startPrefetch, skipPrefetch, prefetchIsFinished, prefetchIsActive } = usePrefetchData({
		dataObjectId: dataObjectID,
		onPrefetchFailureNavigateBack: handleCloseVisualization,
	})
	const setModalityTimesByPatient = useSetRecoilState(modalityTimesAtom)

	function goBack() {
		handleCloseVisualization()
		close()
	}

	function handleCloseVisualization() {
		// Memory Cleanup
		pageManagerRegistry.clear()
		viewScaleRegistry.clear()
		fileScaleRegistry.clear()

		navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}`)

	}

	const [modalities, modalitiesLoaded, getModalitiesUnion] = useRequest(
		`${LINKS.DATA.TRIALS.GET_MODALITIES_UNION.LINK}?parent_study_id=${workspacesProvider.selectedWorkspace}`,
		'JSON',
		() => ({
			method: "POST",
			cache: "no-cache",
			headers: {
				'content-type': 'application/json',
				'Authorization': authProvider.token
			},
			body: JSON.stringify({
				workspace_name: workspacesProvider.selectedWorkspace,
				patient_ids: [],
			})
		})
	)

	const [patientModalities, patientModalitiesLoaded, getModalitiesIntersection] = useRequest(
		`${LINKS.DATA.TRIALS.GET_MODALITIES_UNION.LINK}?parent_study_id=${workspacesProvider.selectedWorkspace}`,
		'JSON',
		() => ({
			method: "POST",
			cache: "no-cache",
			headers: {
				'content-type': 'application/json',
				'Authorization': authProvider.token
			},
			body: JSON.stringify({
				workspace_name: workspacesProvider.selectedWorkspace,
				patient_ids: [patientID],
			})
		})
	)

	const [hotkeysLoaded, setHotkeysLoaded] = useState(false)
	const setGeneralShortcuts = useSetRecoilState(generalShortcutsAtom)
	const setAnnotationShortcuts = useSetRecoilState(annotationShortcutsAtom)

	useOnMount(() => {
		const body = {
			data_object_id: dataObjectID
		}

		endpointProvider.post(LINKS.DATA.PROFILING.GET_ANNOTATION_GROUPS, body)
			.then(annotationGroups => {
				const visibleAnnotationGroups = filterVirtualAnnotationGroups(annotationGroups, environmentVariables)
				setAnnotationGroups(visibleAnnotationGroups)
				const writableAnnotationGroups = visibleAnnotationGroups.filter(group => !group.read_only)

				if (writableAnnotationGroups.length > 0) {
					setActiveAnnotationSession(writableAnnotationGroups[0])
				} else if (canManageDataReview !== false) {
					createAnnotationGroup("Default Session", dataObjectID).then(() => {
						endpointProvider.post(LINKS.DATA.PROFILING.GET_ANNOTATION_GROUPS, body)
							.then(annotationGroups => {
								const visibleAnnotationGroups = filterVirtualAnnotationGroups(annotationGroups, environmentVariables)
								setAnnotationGroups(visibleAnnotationGroups)
								const writableAnnotationGroups = visibleAnnotationGroups.filter(group => !group.read_only)
								if (writableAnnotationGroups.length > 0) {
									setActiveAnnotationSession(writableAnnotationGroups[0])
								} else {
									setActiveAnnotationSession(null)
								}
							})
					}).catch(() => {
						setToastProps({ isOpen: true, severity: "error", message: "Could not create the default session. Please create one in Load Annotations." })
					})
				} else {
					setActiveAnnotationSession(null)
				}
			})
			.catch(error => alert(error))
	})

	const getGeneralShortcuts = useCallback(() => 
		endpointProvider.post(LINKS.ACCOUNT.GET_GENERAL_SHORTCUTS, { workspace_name: workspacesProvider.selectedWorkspace })
			.then(shortcuts => shortcuts)
			.catch(e => console.error(e))
	, [LINKS.ACCOUNT.GET_GENERAL_SHORTCUTS, endpointProvider, setGeneralShortcuts, workspacesProvider.selectedWorkspace])

	const getHotkeys = useCallback(() => 
		Promise.all([getGeneralShortcuts(), getAnnotationShortcuts()])
			.then(([general, annotations]) => {
				setGeneralShortcuts(loadHotkeysFromConfigs(general || []))
				setAnnotationShortcuts(loadHotkeysFromConfigs(annotations || []))
				setHotkeysLoaded(true)
			})
			.catch(e => console.error(e))
	, [getGeneralShortcuts, getAnnotationShortcuts, setGeneralShortcuts, setAnnotationShortcuts])


	const getDataObjectMetadata = useCallback(() => endpointProvider.post(LINKS.VISUALIZE.GET_DATA_OBJECT_BY_ID, { data_object_id: dataObjectID })
		.then(metadata => {
			setDataObjectMetadata(metadata)
		})
		.catch(err => console.error(err))
	, [LINKS.VISUALIZE.GET_DATA_OBJECT_BY_ID, dataObjectID, endpointProvider])

	// TODO: refactor this so it is run onMount. May need to add an endpoint to query that allows fetching modality times based on the dataobjectid
	//       will also need a new endpoint in query to fetch start/end times for analytics or derive the start/end times for each analytic based on modalities used in the computation
	const fetchModalityTimes = useRecoilCallback(({ snapshot }) => async (pid, signal) => {
		const existing = snapshot.getLoadable(modalityTimesAtom).getValue()
		if (existing[pid]) {
			return
		}

		const patientDetails = await endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENT, { patient_primary_key: pid }, undefined, signal)
		if (!patientDetails) {
			return
		}

		const requestBody = {
			project_id: patientDetails.project_id,
			site_id: patientDetails.site_id,
			patient_id: patientDetails.patient_key,
			page_size: 1000,
			filters: {},
			sort: [],
			page: 0,
		}

		const response = await endpointProvider.post(LINKS.DATA.UPLOAD.GET_MODALITY_TIME_BY_PATIENT, requestBody, undefined, signal)
		if (!response || !Array.isArray(response.data)) {
			return
		}

		const normalizedRows = response.data.map(row => ({
			...row,
			min_start_time: new Date(row.min_start_time).getTime(),
			max_end_time: new Date(row.max_end_time).getTime(),
		}))

		setModalityTimesByPatient(previous => ({
			...previous,
			[pid]: normalizedRows,
		}))
	}, [LINKS, endpointProvider, setModalityTimesByPatient])

	useEffect(() => {
		if (!patientID) {
			return
		}

		const abortController = new AbortController()
		fetchModalityTimes(patientID, abortController.signal)
			.catch(error => {
				if (error?.name !== "AbortError") {
					console.error("Failed to fetch modality times", error)
				}
			})

		return () => abortController.abort()
	}, [patientID, fetchModalityTimes])

	const getPersystRecordingInfo = () => {
		if (environmentVariables.FEATURE_FLAG_PERSYST === false) {
			setPersystRecordingInfo(null)
			return
		}

		endpointProvider.post(LINKS.VISUALIZE.PERSYST.GET_RECORDING_INFO,
		{
			workspaceName: workspacesProvider.selectedWorkspace,
			data_object_id: dataObjectID
		})
		.then(recordingInfo => {
			setPersystRecordingInfo({
				recordingStartDate: new Date(recordingInfo["start_time_milliseconds"]),
				recordingEndDate: new Date(recordingInfo["end_time_milliseconds"])
			})
		})
		.catch(err => {
			// Persyst may not be configured for the workspace and that's okay.
			// This is a potential place where we could disable Persyst displays for Data Review.
			console.warn(err)
			setPersystRecordingInfo(null)
		})
	}

	const getLayouts = () => {
		endpointProvider.post(LINKS.DATA.PROFILING.GET_LAYOUTS)
			.then(layoutGroups => {
				const now = Date.now()
				// TODO: remove this when we create a Display JSON validator endpoint
				const layoutGroupsWithDefaults = layoutGroups.map(layoutGroup => ({
					...layoutGroup,
					layouts: layoutGroup.layouts.map(layout => ({
						...layout,
						areas: layout.areas.map(area => {
							if (area.componentId === VisualizationComponent.TIME_SERIES_GROUP) {
								return {
									...area,
									props: {
										...(area.props ?? {}),
										graphs: area.props?.graphs?.map((graph, index) => ({ ...graph, id: graph.id ?? `${now}-${index}`}))
									}
								}
							}
							
							return area
						})
					}))
				}))

				setLayoutGroups(layoutGroupsWithDefaults)
				setLayoutLoaded(true)
			})
			.catch(error => alert("Error with getting layouts: " + error))
	}

	const getEEGMontages = () => {
		endpointProvider.post(LINKS.VISUALIZE.EEG.GET_EEG_MONTAGES)
			.then(eegMontages => {
				setEEGMontages(eegMontages.map(deserializeQueryMontage))
				setEEGMontagesLoaded(true)
			})
			.catch(error => alert("Error with getting EEG Montages: " + error))
	}

	const getPersystMontages = () => {
		if (environmentVariables.FEATURE_FLAG_PERSYST === false) {
			setPersystMontageDefinitions([])
			return
		}
		
		endpointProvider.get(LINKS.DATA.PROFILING.PERSYST.EEG_MONTAGES, { data_object_id: dataObjectID })
			.then(montageGroups => {
				const system = montageGroups.find(group => group.groupName === "System")
	
				if (!system) {
					return
				}
	
				const montages = system.definitions
				setPersystMontageDefinitions(montages)
			})
			.catch(err => {
				console.warn(err)
				setPersystMontageDefinitions([])
			})
	}

	const getEEGMontageMapping = () => {
		endpointProvider.post(LINKS.VISUALIZE.EEG.GET_MONTAGE_MAPPING, { data_object_id: dataObjectID })
			.then(mapping => setEEGMontageMapping(new Map(Object.entries(mapping))))
			.catch(error => {
				let title
				let description

				if (error.toString().includes("CNSDataSource directory doesn't exist")) {
					title = "Missing files"
					description = "There are no files associated with this recording and it is unavailable for review. The files were either moved or deleted."
				} else {
					title = "Server error"
					description = "Error with getting EEG Montage Mapping: " + error
				}

				createModal(
					<ConfirmationModal
						title={title}
						description={description}
						confirmButton={{ text: "Back to patients", theme: MobergTheme.BLUE, onClick: close }}
						showCancelButton={false}
						afterClose={handleCloseVisualization}
					/>
				)
			})
	}

	const getAdmissionInfo = () => {
		const body = {
			"patient_id": patientID
		}

		endpointProvider.post(LINKS.VISUALIZE.GET_ADMISSION_INFO, body).then(result => setAdmissionInfo(result))
	}

	const getPatientMedications = async () => {
		if (environmentVariables.FEATURE_FLAG_ADT_INTEGRATION) {
			emrDataProvider.updatePatientMedications(patientIdentifier)
				.then(setPatientMedications)
				.catch(error => {
					createModal(
						<ConfirmationModal
							title={"Error: EMR Data"}
							description={`${error}`}
							confirmButton={{ 
								text: "Continue without EMR data", 
								theme: MobergTheme.BLUE, 
								onClick: () => setPatientMedications({ harmonized: [], unharmonized: [] })
							}}
							cancelButton={{
								text: "Back to patients", 
								theme: MobergTheme.BLUE, 
								onClick: close
							}}
							afterClose={isCancel => isCancel && handleCloseVisualization()}
						/>
					)
				})
		} else {
			setPatientMedications({ harmonized: [], unharmonized: [] })
		}
	}

	const getAllMedications = () => {
		if (environmentVariables.FEATURE_FLAG_ADT_INTEGRATION) {
			emrDataProvider.updateMedicationList().then(setAllMedications)
		} else {
			setAllMedications([])
		}
	}

    useOnMount(() => {
		getModalitiesUnion(props.patientID).then(modalities => setAllPatientModalities(modalities)).catch(err => console.error(err))
		getModalitiesIntersection(props.patientID).catch(err => console.error(err))
		getHotkeys()
		getDataObjectMetadata()
		resetAnnotations()
		resetSelectedAnnotationGroup()
		resetSelectedAnnotation()
		getLayouts()
		getEEGMontages()
		getEEGMontageMapping()
		getAdmissionInfo()
		getPersystRecordingInfo()
		getPersystMontages()
		getPatientMedications()
		getAllMedications()
	})

	const isReadyToPrefetch =
		modalitiesLoaded &&
		patientModalitiesLoaded &&
		hotkeysLoaded &&
		dataObjectID &&
		patientID &&
		layoutsLoaded &&
		eegMontagesLoaded &&
		eegMontageMapping &&
		workspacesProvider.selectedWorkspace &&
		workspacesProvider.selectedWorkspace.length > 0 &&
		dataObjectMetadata &&
		dataObjectName &&
		dataObjectName.length > 0 &&
		admissionInfo &&
		persystRecordingInfo !== undefined &&
		persystMontageDefinitions !== undefined &&
		patientMedications !== undefined &&
		allMedications !== undefined

	const liveRecordingIsLongEnough = dataObjectMetadata && admissionInfo && (
		!admissionInfo.is_admitted ||
		(dataObjectMetadata["end_time"] ?? 0) - (dataObjectMetadata["start_time"] ?? 0) > MINIMUM_RECORDING_DURATION_LIVE_REVIEW
	)

	// TODO: this staged loading needs a complete overhaul because it regularly causes many bugs
	useEffect(() => {
		if (isReadyToPrefetch && !prefetchIsFinished && !prefetchStarted) {
			setPrefetchStarted(true)
			startPrefetch()
		}

		if (!ready && isReadyToPrefetch && prefetchIsFinished && !completionStarted.current) {
			completionStarted.current = true
			setCompleting(true)

			setTimeout(() => {
				// Clear cached visualization state so the next patient starts with fresh scales/layout state.
				pageManagerRegistry.clear()
				viewScaleRegistry.clear()
				fileScaleRegistry.clear()

				setPatientFileInfo({
					fileStartDate: new Date(dataObjectMetadata["start_time"]),
					fileEndDate: new Date(dataObjectMetadata["end_time"]),
					patientId: patientID,
					patientIdentifier: patientIdentifier,
					dataSourceMap: new Map([
						[DataSource.CURRENT_PATIENT, dataObjectID],
						[DataSource.CPPOPT_ANALYSIS, 999999],
					]),
					patientModalities: Object.keys(patientModalities),
					montageMapping: eegMontageMapping,
					timeZone: dataObjectMetadata["time_zone"] || "America/New_York",
					isAdmitted: admissionInfo.is_admitted,
					persystRecordingInfo,
					persystMontageDefinitions,
					gatewayId: dataObjectMetadata["gateway_id"] ?? undefined,
					patientMedications
				})

				setReady(true)
			}, 800)
		}
	})

	// Show timeout warning if loading takes too long
	useEffect(() => {
		if (ready || completing) return
		const timer = setTimeout(() => setShowTimeoutWarning(true), 25000)
		return () => clearTimeout(timer)
	}, [ready, completing])

	// If the patient is admitted, make sure there's enough data for the live-review delay.
	// Otherwise, there's nothing to see. We want to prevent users from being confused if they see no data.
	useEffect(() => {
		if (!liveReviewWarningActive && ((ready && admissionInfo?.is_admitted && !liveRecordingIsLongEnough) || (dataObjectMetadata && (dataObjectMetadata["start_time"] === null || dataObjectMetadata["end_time"] === null)))) {
			setLiveReviewWarningActive(true)
			createModal(
				<LiveReviewDelayWarning 
					dataObjectId={dataObjectID}
					startTime={dataObjectMetadata["start_time"]}
					endTime={dataObjectMetadata["end_time"]}
					neededTimeMilliseconds={MINIMUM_RECORDING_DURATION_LIVE_REVIEW} 
				/>, 
				{
					afterClose: () => {
						getDataObjectMetadata()
						setTimeout(() => {
							setLiveReviewWarningActive(false)
						}, 5000)
					}
				}
			)
		}
	}, [MINIMUM_RECORDING_DURATION_LIVE_REVIEW, admissionInfo, createModal, dataObjectID, dataObjectMetadata, dataObjectName, getDataObjectMetadata, liveRecordingIsLongEnough, liveReviewWarningActive, ready])

	// Build checklist items in the order they typically resolve
	const checklistItems = [
		{ id: "patient-file", label: "Patient File", loaded: !!dataObjectName },
		{ id: "workspace", label: "Workspace", loaded: !!workspacesProvider.selectedWorkspace },
		{ id: "recording-info", label: "Recording Info", loaded: !!(dataObjectMetadata && liveRecordingIsLongEnough) },
		{ id: "modalities", label: "Modalities", loaded: !!(modalitiesLoaded && patientModalitiesLoaded) },
		{ id: "hotkeys", label: "Hotkeys", loaded: hotkeysLoaded },
		{ id: "layouts", label: "Layouts", loaded: layoutsLoaded },
		{ id: "eeg-montages", label: "EEG Montages", loaded: !!(eegMontagesLoaded && eegMontageMapping) },
	]

	if (environmentVariables.FEATURE_FLAG_PERSYST) {
		checklistItems.push({ id: "persyst", label: "Persyst", loaded: persystRecordingInfo !== undefined && persystMontageDefinitions !== undefined })
	}

	if (environmentVariables.FEATURE_FLAG_ADT_INTEGRATION) {
		checklistItems.push({ label: "Medications", loaded: patientMedications !== undefined && allMedications !== undefined })
	}

	if (!prefetchDisabled) {
		// An in-flight preload is progressing, not stuck: it never turns red and
		// never triggers the timeout warning on its own.
		checklistItems.push({
			id: "preload",
			label: prefetchMessage,
			loaded: prefetchIsFinished,
			active: prefetchIsActive,
		})
	}

	const loadedCount = checklistItems.filter(i => i.loaded).length
	const totalCount = checklistItems.length
	const progress = totalCount > 0 ? loadedCount / totalCount : 0
	const RING_RADIUS = 33
	const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

	return ready && liveRecordingIsLongEnough
		?
		<VisualizationContext.Provider value={{patientID, dataObjectName, siteName, patientName, patient_id: currentPatientFileInfo.patientIdentifier, modalities, patientModalities, hotkeysLoaded, layoutsLoaded,
			closeVisualization: handleCloseVisualization,
			dataObjectID,
		}}>
			<VisualizationArea />
		</VisualizationContext.Provider>
		:
		<div style={{
			display: "flex", width: "100vw", height: "100vh", flexDirection: "column",
			alignItems: "center", justifyContent: "center",
			background: "var(--color-surface-primary)",
			fontFamily: "var(--font-family)",
			position: "relative",
		}}>
			{/* Close button */}
			<div style={{ position: "absolute", top: "20px", right: "20px", zIndex: 1 }}>
				<MobergButton onClick={goBack} shape={MobergButtonShape.SQUARE}>
					<MdIcons.MdOutlineClose size={MobergIconSize.LARGE} />
				</MobergButton>
			</div>

			{/* Content */}
			<div className={completing ? "loading-content loading-content-exit" : "loading-content"} style={{
				maxWidth: "380px",
				width: "calc(100% - 48px)",
				display: "flex", flexDirection: "column", alignItems: "center",
			}}>
				{/* Progress Ring */}
				<div style={{ position: "relative", width: "72px", height: "72px", marginBottom: "32px" }}>
					<svg width="72" height="72" style={{ transform: "rotate(-90deg)", position: "absolute", top: 0, left: 0 }}>
						<circle cx="36" cy="36" r={RING_RADIUS} fill="none" stroke="var(--color-border-light)" strokeWidth="2" opacity="0.5" />
						<circle cx="36" cy="36" r={RING_RADIUS} fill="none" stroke="var(--color-accent-interactive)" strokeWidth="2"
							strokeDasharray={RING_CIRCUMFERENCE}
							strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
							strokeLinecap="round"
							style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(0.4, 0, 0.2, 1)" }}
						/>
					</svg>
					<div style={{
						position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
						display: "flex", alignItems: "center", justifyContent: "center",
					}}>
						<span style={{
							fontSize: "15px", fontWeight: "600",
							color: "var(--text-secondary-color)",
							fontFamily: "var(--header-font-family)",
							transition: "all 0.3s ease",
						}}>
							{Math.round(progress * 100)}%
						</span>
					</div>
				</div>

				{/* Patient info */}
				<h2 style={{
					color: "var(--color-text-primary)", fontSize: "20px",
					fontWeight: "600", margin: "0 0 6px 0", textAlign: "center",
					fontFamily: "var(--header-font-family)", lineHeight: "1.4",
					wordBreak: "break-word", letterSpacing: "-0.01em",
				}}>
					{dataObjectName}
				</h2>
				<p style={{
					color: "var(--text-secondary-color)",
					fontSize: "13px", margin: "0 0 40px 0", textAlign: "center",
					lineHeight: "1.4",
				}}>
					{siteName}{patientIdentifier !== dataObjectName ? ` · ${patientIdentifier}` : ""}
				</p>

				{/* Checklist */}
				<div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0px" }}>
					{checklistItems.map((item, index) => (
						<ChecklistItem
							key={item.id}
							loaded={item.loaded}
							label={item.label}
							index={index}
							error={showTimeoutWarning && isIncompleteChecklistItem(item)}
							completing={completing}
						/>
					))}
				</div>

				{/* Completion message or Skip prefetch */}
				{completing ? (
					<p style={{
						fontSize: "13px", color: "var(--text-secondary-color)",
						marginTop: "32px", fontWeight: "400",
						animation: "fadeIn 0.4s ease-out",
					}}>
						Preparing your session...
					</p>
				) : (
					<div style={{
						marginTop: "28px",
						visibility: (modalitiesLoaded && patientModalitiesLoaded &&
							hotkeysLoaded && layoutsLoaded && eegMontagesLoaded && dataObjectMetadata && workspacesProvider && (isDevelopment || isDemo)) ? "visible" : "hidden"
					}}>
						<MobergButton
							onClick={skipPrefetch}
							variant={MobergButtonVariant.CLEAR}
							theme={MobergTheme.BLUE}
						>
							Skip Prefetch
						</MobergButton>
					</div>
				)}

				{/* Timeout warning — a live preload doesn't count as stuck */}
				{showTimeoutWarning && !completing && checklistItems.some(isIncompleteChecklistItem) && (
					<div style={{
						marginTop: "24px", width: "100%", textAlign: "center",
						animation: "fadeIn 0.3s ease-out",
					}}>
						<p style={{ fontSize: "13px", color: "#FF9800", margin: "0 0 8px 0" }}>
							Some items are taking longer than expected
						</p>
						<span style={{
							fontSize: "13px", color: "var(--color-accent-interactive)",
							cursor: "pointer", fontWeight: "500",
						}} onClick={() => window.location.reload()}>
							Retry
						</span>
					</div>
				)}
			</div>

			<style>{`
				.loading-content {
					animation: contentEnter 0.6s cubic-bezier(0.4, 0, 0.2, 1);
				}
				.loading-content-exit {
					animation: contentExit 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
				}
				@keyframes contentEnter {
					from { transform: translateY(16px); opacity: 0; }
					to { transform: translateY(0); opacity: 1; }
				}
				@keyframes contentExit {
					from { opacity: 1; }
					to { opacity: 0; }
				}
				@keyframes slideUp {
					from { transform: translateY(8px); opacity: 0; }
					to { transform: translateY(0); opacity: 1; }
				}
				@keyframes checkPop {
					0% { transform: scale(0.5); opacity: 0; }
					100% { transform: scale(1); opacity: 1; }
				}
				@keyframes fadeIn {
					from { opacity: 0; }
					to { opacity: 1; }
				}
				@keyframes spin {
					to { transform: rotate(360deg); }
				}
				.item-spinner {
					width: 16px;
					height: 16px;
					border: 1.5px solid var(--color-border-light);
					border-top-color: var(--color-accent-interactive);
					border-radius: 50%;
					animation: spin 0.9s linear infinite;
				}
			`}</style>
		</div>
}

const ChecklistItem = ({ loaded, label, index, error }) => (
	<div style={{
		display: "flex", alignItems: "center", gap: "12px",
		padding: "10px 0",
		borderBottom: "1px solid var(--color-border-light)",
		animation: `slideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${index * 50}ms both`,
	}}>
		{/* Status icon */}
		<div style={{
			width: "20px", height: "20px",
			display: "flex", alignItems: "center", justifyContent: "center",
			flexShrink: 0,
		}}>
			{loaded ? (
				<div style={{ animation: "checkPop 0.25s cubic-bezier(0.4, 0, 0.2, 1)" }}>
					<MdIcons.MdCheck color="var(--color-accent-interactive)" size={18} />
				</div>
			) : error ? (
				<MdIcons.MdClose color="var(--color-danger-text)" size={16} />
			) : (
				<div className="item-spinner" />
			)}
		</div>

		{/* Label */}
		<span style={{
			fontSize: "14px",
			fontWeight: loaded ? "500" : "400",
			color: error ? "var(--color-danger-text)" : loaded ? "var(--color-text-primary)" : "var(--text-secondary-color)",
			transition: "color 0.3s ease, font-weight 0.3s ease",
			flex: 1,
		}}>
			{label}
		</span>

		{/* Status text */}
		{error ? (
			<span style={{
				fontSize: "12px", color: "var(--color-accent-interactive)",
				cursor: "pointer", fontWeight: "500",
			}} onClick={() => window.location.reload()}>
				Retry
			</span>
		) : loaded ? (
			<span style={{ fontSize: "12px", color: "var(--text-secondary-color)", opacity: 0.6 }}>
				Done
			</span>
		) : null}
	</div>
)
